/**
 * What a build registered, turned into things a screen can say.
 *
 * Lives in `lib/` rather than in a skin because every skin asks the same two
 * questions of the same map — what did this build ship, and which of those are
 * worth a headline — and a skin that answered them for itself would eventually
 * describe the same tenant differently from the one beside it.
 *
 * The whole file exists on top of one rule the contract sets and this module
 * must not soften: an ABSENT count means Hatch could not establish it, and a
 * present `0` means the build registered none of that kind. Both are rendered
 * as nothing here, but for different reasons — the first because there is
 * nothing to say, the second because "0 triggers" beside "3 tables" reads as a
 * missing feature rather than as a site that never needed one.
 */

import {
  BACKEND_OBJECT_KINDS,
  BACKEND_OBJECT_LABELS,
  type BackendObjectKind,
  type BackendObjects,
} from "@hatch/shared";

/** One primitive the build actually shipped, ready to render. */
export interface BuiltObject {
  kind: BackendObjectKind;
  count: number;
  /** Plural-correct and shared: "3 Postgres tables", "1 realtime channel". */
  label: string;
  /** Just the noun, for a tile that shows the number separately. */
  noun: string;
}

/**
 * The three a visitor who has never opened Xano recognises as "a backend".
 *
 * They get full tiles; everything else gets a line. That split is not a
 * judgement about which primitives matter — it is about what a stranger can
 * read in the two seconds they look at a booth monitor. Data, the endpoints
 * over it, and the logic they share are legible cold; "2 addons" is not, and a
 * grid of seventeen equal tiles is a spreadsheet rather than a headline.
 */
export const HEADLINE_KINDS = ["tables", "apis", "functions"] as const;

/**
 * Every kind this build shipped at least one of, in the contract's order.
 *
 * Zero is filtered out deliberately, and it is the interesting half of the
 * rule: `countBackendObjects` reports a real `0` for a kind whose absence it is
 * sure of, which is honest data and terrible copy. A build is described by what
 * it has.
 */
export function builtObjects(objects: BackendObjects | null | undefined): BuiltObject[] {
  if (!objects) return [];
  return BACKEND_OBJECT_KINDS.flatMap((kind) => {
    const count = objects[kind];
    if (count === undefined || count <= 0) return [];
    const label = BACKEND_OBJECT_LABELS[kind];
    const noun = count === 1 ? label.one : label.many;
    return [{ kind, count, label: `${String(count)} ${noun}`, noun }];
  });
}

/** The headline three, in the order `HEADLINE_KINDS` fixes. */
export function headlineObjects(objects: BackendObjects | null | undefined): BuiltObject[] {
  const built = builtObjects(objects);
  return HEADLINE_KINDS.flatMap((kind) => built.filter((o) => o.kind === kind));
}

/** Everything else, in the contract's order — the overflow line's contents. */
export function otherObjects(objects: BackendObjects | null | undefined): BuiltObject[] {
  const headline = new Set<string>(HEADLINE_KINDS);
  return builtObjects(objects).filter((o) => !headline.has(o.kind));
}
